<template>
  <button
    type="button"
    data-copy-to-clipboard
    :class="[structure['root'], skin['root'], interaction['root']]"
    :aria-label="label"
    :disabled="isCopying"
    @click="handleClick"
  >
    <CheckIcon
      v-if="copied"
      class="size-4 text-success-600"
      aria-hidden="true"
    />

    <DocumentDuplicateIcon
      v-else
      class="size-4"
      aria-hidden="true"
    />
  </button>

  <LiveRegion
    :message="announcement"
    :role="announcementRole"
    :politeness="announcementRole === 'alert' ? 'assertive' : 'polite'"
  />
</template>

<script setup lang="ts">
import { CheckIcon, DocumentDuplicateIcon } from '@heroicons/vue/20/solid'
import { nextTick, onUnmounted, ref } from 'vue'

import LiveRegion from '../LiveRegion/LiveRegion.atom.vue'
import { copyTextToClipboard } from '~/utils/copyTextToClipboard'

import structure from './CopyToClipboard.structure.module.css'
import skin from './CopyToClipboard.skin.module.css'
import interaction from './CopyToClipboard.interaction.module.css'

const props = defineProps<{
  /** Text copied to the clipboard on click. */
  text: string
  /** Accessible name for the button. */
  label: string
  /** Already-translated text announced on a successful copy. */
  copiedMessage: string
  /** Already-translated text announced when the copy fails. */
  failedMessage: string
}>()

const copied = ref(false)
const isCopying = ref(false)
let copiedTimer: ReturnType<typeof setTimeout> | null = null

const announcement = ref('')
const announcementRole = ref<'alert' | 'status'>('status')

// Resets before re-announcing so two consecutive identical messages both fire.
const announce = async (message: string, role: 'alert' | 'status'): Promise<void> => {
  announcement.value = ''
  await nextTick()
  announcementRole.value = role
  announcement.value = message
}

const handleClick = async (): Promise<void> => {
  isCopying.value = true
  try {
    const success = await copyTextToClipboard(props.text)
    if (success) {
      copied.value = true
      if (copiedTimer) clearTimeout(copiedTimer)
      copiedTimer = setTimeout(() => {
        copied.value = false
      }, 2000)
      await announce(props.copiedMessage, 'status')
    } else {
      await announce(props.failedMessage, 'alert')
    }
  } finally {
    isCopying.value = false
  }
}

onUnmounted(() => {
  if (copiedTimer) clearTimeout(copiedTimer)
})
</script>
